import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { useUser } from "../context/UserContext";
import { useApi } from "../utils/Api";
import { useScrollLock } from "../hooks/useScrollLock";
import { POLL_OPEN_EVENT, POLL_OPTIONS, isPollActiveHere, markPollVoted } from "../utils/poll";

// Pages where a pop-up would get in the way of an order
const HIDDEN_PATHS = ["/checkout", "/payment-success", "/order-tracking"];
const SHOW_DELAY_MS = 1500;
const RESULTS_REFRESH_MS = 5000;

const PollModal = () => {
  const { user, userLoading, showAuthModal, openLoginModal } = useUser();
  const { apiFetch } = useApi();
  const location = useLocation();

  const [visible, setVisible] = useState(false);
  const [view, setView] = useState("vote"); // "vote" | "results"
  const [selected, setSelected] = useState(null);
  const [myChoice, setMyChoice] = useState(null);
  const [myPhone, setMyPhone] = useState("");
  const [results, setResults] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const apiFetchRef = useRef(apiFetch);
  apiFetchRef.current = apiFetch;
  const startedRef = useRef(false);
  const pendingChoiceRef = useRef(null);
  const userIdRef = useRef(null);
  userIdRef.current = user?.id;

  const onHiddenPath = HIDDEN_PATHS.some((p) => location.pathname.startsWith(p));
  useScrollLock(visible && !onHiddenPath);

  const showResults = useCallback((res, choice) => {
    markPollVoted(userIdRef.current);
    setResults(res?.results || null);
    setMyChoice(res?.choice ?? choice ?? null);
    setMyPhone(res?.phone || "");
    setView("results");
  }, []);

  // Decide once per page load, after the login has been restored, whether to show the poll
  useEffect(() => {
    if (userLoading || startedRef.current) return undefined;
    startedRef.current = true;
    if (!isPollActiveHere(user?.id)) return undefined;

    let cancelled = false;
    let timer = null;
    const show = () => {
      timer = setTimeout(() => {
        if (!cancelled) setVisible(true);
      }, SHOW_DELAY_MS);
    };

    if (!user) {
      show();
    } else {
      // Logged in: skip the poll if they already voted (on this or another device)
      apiFetchRef
        .current("poll-status")
        .then((res) => {
          if (cancelled) return;
          if (res?.success && !res.open) return;
          if (res?.success && res.voted) {
            markPollVoted(user.id);
            return;
          }
          show();
        })
        .catch(() => show());
    }

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [userLoading, user]);

  const submitVote = useCallback(
    async (choice) => {
      setSubmitting(true);
      setError("");
      try {
        const res = await apiFetchRef.current("poll-vote", { choice });
        if (res?.success || res?.already_voted) {
          showResults(res, choice);
        } else {
          setError(res?.message || res?.error || "Sorry, we couldn't record your vote. Please try again.");
        }
      } catch {
        setError("Sorry, we couldn't record your vote. Please try again.");
      } finally {
        setSubmitting(false);
      }
    },
    [showResults],
  );

  // They chose a dish while logged out: once they have logged in, cast that vote
  useEffect(() => {
    if (!visible || !user || view !== "vote") return;

    if (pendingChoiceRef.current) {
      const choice = pendingChoiceRef.current;
      pendingChoiceRef.current = null;
      submitVote(choice);
      return;
    }

    // Logged in without voting yet: they may already have voted on another device
    apiFetchRef
      .current("poll-status")
      .then((res) => {
        if (res?.success && res.voted) showResults(res, null);
      })
      .catch(() => {});
  }, [visible, user, view, submitVote, showResults]);

  // Closing the login box without logging in cancels the waiting vote
  useEffect(() => {
    if (!showAuthModal && !user) pendingChoiceRef.current = null;
  }, [showAuthModal, user]);

  // Live results while the results screen is open
  useEffect(() => {
    if (!visible || view !== "results") return undefined;
    const timer = setInterval(() => {
      apiFetchRef
        .current("poll-status")
        .then((res) => {
          if (res?.success && res.results) setResults(res.results);
        })
        .catch(() => {});
    }, RESULTS_REFRESH_MS);
    return () => clearInterval(timer);
  }, [visible, view]);

  // The "Vote now" link on the home page opens the poll again after it was closed
  useEffect(() => {
    const open = () => setVisible(true);
    window.addEventListener(POLL_OPEN_EVENT, open);
    return () => window.removeEventListener(POLL_OPEN_EVENT, open);
  }, []);

  const handleVote = () => {
    if (!selected || submitting) return;
    if (!user) {
      pendingChoiceRef.current = selected;
      setError("");
      openLoginModal();
      return;
    }
    submitVote(selected);
  };

  const close = () => setVisible(false);

  if (!visible || onHiddenPath) return null;

  // Highest percentage first (ties keep the dish order)
  const optionRows = POLL_OPTIONS.map((opt) => {
    const stats = results?.options?.find((o) => o.choice === opt.id) || { votes: 0, percent: 0 };
    return { ...opt, ...stats };
  }).sort((a, b) => b.votes - a.votes || a.id - b.id);

  return createPortal(
    <div className="fixed inset-0 z-[9000] flex items-center justify-center bg-black/60 px-3 [@media(min-height:820px)]:px-4 backdrop-blur-lg" onClick={close}>
      <div
        className="relative max-h-[92vh] supports-[height:1dvh]:max-h-[92dvh] w-full max-w-[460px] overflow-y-auto rounded-2xl border border-white/10 bg-[#1A1A1A] px-5 pb-4 pt-5 [@media(min-height:820px)]:px-7 [@media(min-height:820px)]:pb-6 [@media(min-height:820px)]:pt-7 font-poppins text-white shadow-[0_20px_60px_rgba(0,0,0,0.5)] animate-fade-in-scale"
        onClick={(e) => e.stopPropagation()}
      >
        <div
          aria-hidden="true"
          className="absolute inset-x-0 top-0 h-1.5 bg-gradient-to-r from-[#E31B23] via-[#FFC72C] to-[#009B3A]"
        />
        <button
          type="button"
          onClick={close}
          aria-label="Close"
          className="absolute right-2.5 top-3.5 flex h-11 w-11 cursor-pointer items-center justify-center rounded-full bg-transparent text-[34px] leading-none text-white/70 transition hover:bg-white/10 hover:text-white"
        >
          &times;
        </button>

        {view === "vote" ? (
          <>
            <p className="text-[10px] [@media(min-height:820px)]:text-[12px] font-semibold uppercase tracking-[0.12em] [@media(min-height:820px)]:tracking-[0.18em] text-brand-orange">Black History Month <span className="px-0.5 text-white/40">&middot;</span> Menu taster</p>
            <h2 className="mt-1.5 [@media(min-height:820px)]:mt-2 pr-10 font-prata text-[22px] [@media(min-height:820px)]:text-[26px] font-bold leading-tight">You decide!</h2>
            <p className="mt-2.5 [@media(min-height:820px)]:mt-3.5 text-[14px] [@media(min-height:820px)]:text-[15px] text-white/80">Which new dish would you love Lasgidi to introduce?</p>

            <div className="mt-4 [@media(min-height:820px)]:mt-6 flex flex-col gap-2.5 [@media(min-height:820px)]:gap-3" role="radiogroup" aria-label="New dish">
              {POLL_OPTIONS.map((opt) => {
                const active = selected === opt.id;
                return (
                  <button
                    key={opt.id}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    onClick={() => setSelected(opt.id)}
                    className={`flex w-full cursor-pointer items-center gap-2.5 [@media(min-height:820px)]:gap-3 rounded-xl border px-3 [@media(min-height:820px)]:px-4 py-3 [@media(min-height:820px)]:py-3.5 text-left transition ${
                      active ? "border-brand-orange bg-brand-orange/10" : "border-white/15 bg-white/5 hover:border-brand-orange/60 hover:bg-white/10"
                    }`}
                  >
                    <span
                      className={`flex h-6 w-6 [@media(min-height:820px)]:h-7 [@media(min-height:820px)]:w-7 shrink-0 items-center justify-center rounded-full text-[12px] [@media(min-height:820px)]:text-[13px] font-bold ${
                        active ? "bg-brand-orange text-black" : "bg-white/10 text-white/80"
                      }`}
                    >
                      {opt.id}
                    </span>
                    <span className="text-[14px] [@media(min-height:820px)]:text-[15px] font-medium">{opt.name}</span>
                  </button>
                );
              })}
            </div>

            <div className="mt-4 [@media(min-height:820px)]:mt-6 rounded-xl border border-brand-orange/40 bg-brand-orange/10 px-3 py-2 [@media(min-height:820px)]:px-4 [@media(min-height:820px)]:py-3 text-[13px] leading-snug [@media(min-height:820px)]:leading-relaxed text-white/90">
              <span className="font-semibold text-brand-orange">Raffle draw:</span> By voting, your phone number is
              entered into our raffle draw to win a freebie at our taster session on Sunday 25 October 2026.
            </div>

            {error && <p className="mt-3 text-[13px] text-red-400">{error}</p>}

            <button
              type="button"
              onClick={handleVote}
              disabled={!selected || submitting}
              className="mt-4 [@media(min-height:820px)]:mt-6 flex w-full cursor-pointer items-center justify-center rounded-full bg-brand-orange px-5 py-3 [@media(min-height:820px)]:py-3.5 text-[15px] font-bold text-black transition hover:bg-[#e07010] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-brand-orange"
            >
              {submitting ? "Sending your vote..." : user ? "Vote" : "Log in to vote"}
            </button>

            <p className="mt-4 hidden text-center text-[11px] uppercase tracking-[0.14em] text-white/35 [@media(min-height:820px)]:block">
              Your Lasgidi. Your Taste. Your Choice
            </p>
          </>
        ) : (
          <>
            <p className="text-[10px] [@media(min-height:820px)]:text-[12px] font-semibold uppercase tracking-[0.12em] [@media(min-height:820px)]:tracking-[0.18em] text-brand-orange">Thanks for voting</p>
            <h2 className="mt-1.5 [@media(min-height:820px)]:mt-2 pr-10 font-prata text-[22px] [@media(min-height:820px)]:text-[26px] font-bold leading-tight">Live results</h2>
            <p className="mt-2.5 [@media(min-height:820px)]:mt-3.5 text-[13px] [@media(min-height:820px)]:text-[14px] text-white/75">
              Your phone number{myPhone && <strong className="font-semibold text-white"> {myPhone}</strong>} has been
              entered into the raffle draw. Good luck!
            </p>

            <div className="mt-6 [@media(min-height:820px)]:mt-8 flex flex-col gap-3.5 [@media(min-height:820px)]:gap-4">
              {optionRows.map((opt) => {
                const mine = myChoice === opt.id;
                return (
                  <div key={opt.id}>
                    <div className="mb-1.5 flex items-baseline justify-between gap-3 text-[14px]">
                      <span className="font-medium">
                        {opt.name}
                        {mine && (
                          <span className="ml-2 rounded-full bg-brand-orange/15 px-2 py-0.5 text-[11px] font-semibold text-brand-orange">
                            Your pick
                          </span>
                        )}
                      </span>
                      <span className="shrink-0 font-semibold">{opt.percent}%</span>
                    </div>
                    <div className="h-2.5 w-full overflow-hidden rounded-full bg-white/10">
                      <div
                        className={`h-full rounded-full transition-all duration-700 ${mine ? "bg-brand-orange" : "bg-white/40"}`}
                        style={{ width: `${opt.percent}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
};

export default PollModal;
